import React, { useEffect } from 'react'

import { type PayrollProviderConfig } from 'utils/web_api.schemas'

import { PayrollProviderGrid } from './PayrollProviderGrid'
import { PayrollProviderSearch } from './PayrollProviderSearch'
import { createPayrollProviderStrategies } from './payrollProviderStrategies'
import { usePayrollProviderSelection } from '../hooks/usePayrollProviderSelection'

type ProviderCapabilities = {
  supportsAutomatedDeductions: boolean;
  supportsPaySchedules: boolean;
}
export type OnSuccessCallback = (providerCapabilities: ProviderCapabilities) => void
export type OnFailureCallback = () => void

const PayrollConnectionPicker = ({
  payrollProviders,
  promotedPayrollProviders,
  onSuccessCallback,
  onSuccessUrl,
  onFailureCallback,
  onFailureUrl,
}: {
  payrollProviders: PayrollProviderConfig[];
  promotedPayrollProviders: PayrollProviderConfig[];
  onSuccessCallback: OnSuccessCallback;
  onSuccessUrl: string;
  onFailureCallback?: OnFailureCallback;
  onFailureUrl?: string;
}): React.ReactElement => {
  const {
    payrollProviderSearchRef,
    handleProviderSelection,
  } = usePayrollProviderSelection()

  const providerStrategies = createPayrollProviderStrategies({
    onSuccessCallback,
    onSuccessUrl,
    onFailureCallback,
    onFailureUrl
  })

  const connectToPayrollProvider = async (
    payrollProviderConfig: PayrollProviderConfig
  ) => {
    return handleProviderSelection(
      payrollProviderConfig,
      providerStrategies,
      payrollProviderConfig.supports_automatic_deductions || false
    )
  }

  useEffect(() => {
    // Open modals when their conditions are met
    Object.values(providerStrategies).forEach((strategy) => {
      if (strategy.isModalReady()) {
        strategy.openModal()
        strategy.cleanup()
      }
    })
  }, [providerStrategies])

  return (
    <>
      <PayrollProviderSearch
        payrollProviders={payrollProviders}
        ref={payrollProviderSearchRef}
        onOpen={connectToPayrollProvider}
      />
      <PayrollProviderGrid
        promotedProviders={promotedPayrollProviders}
        onSelectProvider={connectToPayrollProvider}
        onClickOther={() => payrollProviderSearchRef.current?.focus()}
      />
    </>
  )
}

export default PayrollConnectionPicker
